import * as React from 'react';

import { AsyncBoundary, cn, Col, Row } from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { LoadDetail } from './LoadDetail';
import { LoadsSidebar } from './LoadsSidebar';

import {
  $loadsTabContext,
  $loadsTabDetail,
  $remoteLoads,
  $selectedLoadId,
} from '~/modules/trade/_state/loadsTab.state';
import { useTranslations } from '~/modules/trade/i18n/useTranslations';

type LoadsTabProps = {
  bookingId?: string;
  salesOrderId?: string;
  purchaseOrderId?: string;
  entityType: 'booking' | 'salesOrder' | 'purchaseOrder';
  disableAddLoad?: boolean;
  className?: string;
};

// ⚛️ ⚛️ ⚛️ LOADS TAB ⚛️ ⚛️ ⚛️
export const LoadsTab: React.FC<LoadsTabProps> = ({
  bookingId,
  salesOrderId,
  purchaseOrderId,
  entityType,
  disableAddLoad = false,
  className,
}) => {
  const t = useTranslations();

  // Atoms
  const selectedLoadId = useAtomValue($selectedLoadId);
  const {
    data: detailData,
    isLoading: isDetailLoading,
    refetch: refetchDetail,
  } = useAtomValue($loadsTabDetail);
  const { fetch: fetchLoads, data: loadsData } = useAtomValue($remoteLoads);

  const setLoadsTabContext = useSetAtom($loadsTabContext);
  const setSelectedLoadId = useSetAtom($selectedLoadId);

  const loadProps = detailData?.load ?? null;

  // Callbacks
  const handleRefetch = React.useCallback(() => {
    fetchLoads({
      ...(entityType === 'booking' && { bookingId }),
      ...(entityType === 'salesOrder' && { salesOrderId }),
      ...(entityType === 'purchaseOrder' && { purchaseOrderId }),
    });
    if (selectedLoadId) {
      refetchDetail();
    }
  }, [
    fetchLoads,
    refetchDetail,
    entityType,
    bookingId,
    salesOrderId,
    purchaseOrderId,
    selectedLoadId,
  ]);

  // Effects
  /** Sync props into loadsTabContext atom */
  React.useEffect(() => {
    setLoadsTabContext({
      bookingId,
      salesOrderId,
      purchaseOrderId,
      entityType,
    });
    return () => {
      setLoadsTabContext(null);
    };
  }, [
    setLoadsTabContext,
    bookingId,
    salesOrderId,
    purchaseOrderId,
    entityType,
  ]);

  /** Reset selection when the parent entity changes */
  React.useEffect(() => {
    setSelectedLoadId(null);
  }, [setSelectedLoadId, bookingId, salesOrderId, purchaseOrderId]);

  return (
    <Row
      gap4
      yStart
      className={cn('@LoadsTab', 'h-full overflow-y-hidden p-5', className)}
    >
      <LoadsSidebar disableAddLoad={disableAddLoad} />

      {loadProps && selectedLoadId && (
        <Col yStart className="flex-1 overflow-y-auto">
          <AsyncBoundary loading={isDetailLoading} error={null}>
            <LoadDetail
              load={loadProps}
              entityType={entityType}
              onRefetch={handleRefetch}
            />
          </AsyncBoundary>
        </Col>
      )}

      {!!loadsData?.loads.length && !selectedLoadId && (
        <Col className="h-full flex-1 self-center" xCenter yCenter>
          <p className="text-neutral/50">{t.loadDetail.loadsTab.selectLoad}</p>
        </Col>
      )}
    </Row>
  );
};
